import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { parseFormulaPrecedents, getActiveSelection, navigateToCell, readPrecedentValuesAndFormulas, readDirectDependents, checkCellsHaveDependents, } from "../../shared/utils";
const TRACE_NAVIGATION_GUARD_MS = 1500;
function normalizeSelectionAddress(address) {
    return address.replace(/\$/g, "").toUpperCase();
}
const TraceContext = createContext(null);
export function useTraceContext() {
    const ctx = useContext(TraceContext);
    if (!ctx)
        throw new Error("useTraceContext must be used within TraceProvider");
    return ctx;
}
export function TraceProvider({ children }) {
    const [traceOpen, setTraceOpen] = useState(false);
    const [traceData, setTraceData] = useState(null);
    const [traceSelectedIndex, setTraceSelectedIndex] = useState(0);
    const [traceMode, setTraceMode] = useState("precedents");
    const traceNavRef = useRef({ index: 0, cancelled: false });
    const traceDataRef = useRef(traceData);
    traceDataRef.current = traceData;
    const traceOpenRef = useRef(traceOpen);
    traceOpenRef.current = traceOpen;
    const traceModeRef = useRef(traceMode);
    traceModeRef.current = traceMode;
    const traceSelectedIndexRef = useRef(traceSelectedIndex);
    traceSelectedIndexRef.current = traceSelectedIndex;
    const expandingRef = useRef(false);
    const liveRefreshTimerRef = useRef(null);
    const pendingTraceNavigationRef = useRef(null);
    // Bumped on every open and on close; an in-flight open whose generation is
    // stale must not touch state (e.g. reopen a panel the user just dismissed).
    const openGenRef = useRef(0);
    const navigateWithinTrace = useCallback(async (ref, sheetName) => {
        pendingTraceNavigationRef.current = {
            address: normalizeSelectionAddress(ref),
            sheet: sheetName.toUpperCase(),
            expiresAt: Date.now() + TRACE_NAVIGATION_GUARD_MS,
        };
        await navigateToCell(ref, sheetName);
    }, []);
    const shouldIgnoreTraceNavigationSelection = useCallback(async () => {
        const pending = pendingTraceNavigationRef.current;
        if (!pending)
            return false;
        if (Date.now() > pending.expiresAt) {
            pendingTraceNavigationRef.current = null;
            return false;
        }
        const selection = await getActiveSelection();
        if (!selection)
            return true;
        if (normalizeSelectionAddress(selection.address) === pending.address
            && selection.sheet.toUpperCase() === pending.sheet) {
            pendingTraceNavigationRef.current = null;
            return true;
        }
        pendingTraceNavigationRef.current = null;
        return false;
    }, []);
    const openTraceWithData = useCallback((data) => {
        setTraceData(data);
        setTraceOpen(true);
        setTraceSelectedIndex(0);
        setTraceMode(data.mode || "precedents");
    }, []);
    const handleOpenTrace = useCallback(async () => {
        const gen = ++openGenRef.current;
        try {
            const sel = await getActiveSelection();
            if (gen !== openGenRef.current)
                return;
            if (!sel) {
                console.warn("Select a cell to trace precedents.");
                return;
            }
            const hasFormula = !!sel.formula && sel.formula.startsWith("=");
            const rawPrecedents = hasFormula
                ? parseFormulaPrecedents(sel.formula, sel.sheet)
                : [];
            if (rawPrecedents.length === 0) {
                traceNavRef.current = { index: 0, cancelled: false };
                openTraceWithData({
                    formula: hasFormula ? sel.formula : null,
                    originCell: sel.cell,
                    originSheet: sel.sheet,
                    originValue: sel.value,
                    precedents: [],
                    mode: "precedents",
                });
                return;
            }
            const { values, formulas } = await readPrecedentValuesAndFormulas(rawPrecedents);
            if (gen !== openGenRef.current)
                return;
            const originNodeId = `${sel.sheet}!${sel.cell}`.toUpperCase();
            const precedents = rawPrecedents.map((p, i) => {
                const nodeId = `${p.sheet}!${p.ref}`.toUpperCase();
                const ancestorIds = [originNodeId];
                const isCircular = ancestorIds.includes(nodeId);
                const formula = isCircular ? null : formulas[i];
                return {
                    ...p,
                    value: values[i],
                    depth: 1,
                    expanded: false,
                    childCount: 0,
                    nodeId,
                    treeKey: String(i),
                    ancestorIds,
                    isCircular,
                    hasFormula: !p.isRange && !isCircular && formula != null,
                    formula,
                };
            });
            traceNavRef.current = { index: 0, cancelled: false };
            openTraceWithData({
                formula: sel.formula,
                originCell: sel.cell,
                originSheet: sel.sheet,
                originValue: sel.value,
                precedents,
                mode: "precedents",
            });
        }
        catch (err) {
            console.error("Trace error:", err);
        }
    }, [openTraceWithData]);
    const handleGoHome = useCallback(() => {
        openGenRef.current++;
        traceNavRef.current = { ...traceNavRef.current, cancelled: true };
        setTraceOpen(false);
        setTraceData(null);
        setTraceSelectedIndex(0);
        setTraceMode("precedents");
    }, []);
    const handleCloseTrace = useCallback(async () => {
        const data = traceDataRef.current;
        openGenRef.current++;
        traceNavRef.current = { ...traceNavRef.current, cancelled: true };
        setTraceOpen(false);
        setTraceData(null);
        setTraceSelectedIndex(0);
        setTraceMode("precedents");
        if (data) {
            await navigateWithinTrace(data.originCell, data.originSheet);
        }
        if (Office.addin?.hide) {
            await Office.addin.hide();
        }
    }, [navigateWithinTrace]);
    const handleTraceSelect = useCallback(async (index) => {
        setTraceSelectedIndex(index);
        traceNavRef.current = { index, cancelled: false };
        await new Promise((r) => setTimeout(r, 50));
        const nav = traceNavRef.current;
        if (nav.cancelled || nav.index !== index)
            return;
        const data = traceDataRef.current;
        if (!data)
            return;
        if (index === 0) {
            await navigateWithinTrace(data.originCell, data.originSheet);
        }
        else {
            const prec = data.precedents[index - 1];
            await navigateWithinTrace(prec.ref, prec.sheet);
        }
    }, [navigateWithinTrace]);
    const handleExpandNode = useCallback(async (flatIndex) => {
        if (expandingRef.current)
            return;
        expandingRef.current = true;
        traceNavRef.current = { ...traceNavRef.current, cancelled: true };
        try {
            const data = traceDataRef.current;
            if (!data)
                return;
            const node = data.precedents[flatIndex];
            if (!node || !node.hasFormula || node.expanded)
                return;
            const childRefs = parseFormulaPrecedents(node.formula, node.sheet);
            if (childRefs.length === 0)
                return;
            const { values, formulas } = await readPrecedentValuesAndFormulas(childRefs);
            const currentData = traceDataRef.current;
            if (!currentData)
                return;
            const currentNode = currentData.precedents[flatIndex];
            if (!currentNode || currentNode.nodeId !== node.nodeId || currentNode.expanded)
                return;
            const children = childRefs.map((child, j) => {
                const nodeId = `${child.sheet}!${child.ref}`.toUpperCase();
                const ancestorIds = [...(node.ancestorIds || []), node.nodeId];
                const isCircular = ancestorIds.includes(nodeId);
                const formula = isCircular ? null : formulas[j];
                return {
                    ...child,
                    value: values[j],
                    depth: (node.depth || 1) + 1,
                    expanded: false,
                    childCount: 0,
                    nodeId,
                    treeKey: `${node.treeKey}/${j}`,
                    ancestorIds,
                    isCircular,
                    hasFormula: !child.isRange && !isCircular && formula != null,
                    formula,
                };
            });
            const precs = [...currentData.precedents];
            precs[flatIndex] = { ...precs[flatIndex], expanded: true, childCount: children.length };
            precs.splice(flatIndex + 1, 0, ...children);
            setTraceData({ ...currentData, precedents: precs });
            const currentSel = traceSelectedIndexRef.current;
            const adjustedSel = currentSel > flatIndex + 1 ? currentSel + children.length : currentSel;
            setTraceSelectedIndex(adjustedSel);
            traceNavRef.current = { index: adjustedSel, cancelled: false };
        }
        finally {
            expandingRef.current = false;
        }
    }, []);
    const handleCollapseNode = useCallback(async (flatIndex) => {
        traceNavRef.current = { ...traceNavRef.current, cancelled: true };
        const data = traceDataRef.current;
        if (!data)
            return;
        const node = data.precedents[flatIndex];
        if (!node || !node.expanded)
            return;
        const nodeDepth = node.depth ?? 0;
        let removeCount = 0;
        for (let i = flatIndex + 1; i < data.precedents.length; i++) {
            if ((data.precedents[i].depth ?? 0) > nodeDepth)
                removeCount++;
            else
                break;
        }
        const precs = [...data.precedents];
        precs.splice(flatIndex + 1, removeCount);
        precs[flatIndex] = { ...precs[flatIndex], expanded: false, childCount: 0 };
        const currentSel = traceSelectedIndexRef.current;
        let newSel = currentSel;
        const removedEnd = flatIndex + removeCount;
        if (currentSel > 0) {
            const selFlat = currentSel - 1;
            if (selFlat > flatIndex && selFlat <= removedEnd) {
                newSel = flatIndex + 1;
            }
            else if (selFlat > removedEnd) {
                newSel = currentSel - removeCount;
            }
        }
        setTraceData({ ...data, precedents: precs });
        setTraceSelectedIndex(newSel);
        traceNavRef.current = { index: newSel, cancelled: false };
        if (newSel !== currentSel) {
            // Navigate using the post-collapse array; traceDataRef still holds the
            // pre-collapse array until React re-renders.
            if (newSel === 0) {
                await navigateWithinTrace(data.originCell, data.originSheet);
            }
            else {
                const prec = precs[newSel - 1];
                if (prec)
                    await navigateWithinTrace(prec.ref, prec.sheet);
            }
        }
    }, [navigateWithinTrace]);
    const handleOpenDependents = useCallback(async () => {
        const gen = ++openGenRef.current;
        try {
            const sel = await getActiveSelection();
            if (gen !== openGenRef.current)
                return;
            if (!sel) {
                console.warn("Select a cell to trace dependents.");
                return;
            }
            if (sel.address.includes(":") || sel.address.includes(",")) {
                console.warn("Select a single cell to trace dependents.");
                return;
            }
            const { status, items } = await readDirectDependents(sel.address, sel.sheet);
            if (gen !== openGenRef.current)
                return;
            if (status === "unsupported") {
                console.warn("Dependents tracing requires Microsoft 365 (version 2108 or later).");
                return;
            }
            if (status === "error") {
                console.warn("Could not read dependents \u2014 check that the workbook is saved and try again.");
                return;
            }
            if (status === "empty" || items.length === 0) {
                traceNavRef.current = { index: 0, cancelled: false };
                openTraceWithData({
                    formula: null,
                    originCell: sel.address,
                    originSheet: sel.sheet,
                    originValue: sel.value,
                    precedents: [],
                    mode: "dependents",
                });
                return;
            }
            const { values, formulas } = await readPrecedentValuesAndFormulas(items);
            const singleCells = items.filter((p) => !p.isRange);
            const hasDeps = await checkCellsHaveDependents(singleCells);
            if (gen !== openGenRef.current)
                return;
            const hasDepsMap = new Map(singleCells.map((p, i) => [`${p.sheet}!${p.ref}`.toUpperCase(), hasDeps[i]]));
            const originNodeId = `${sel.sheet}!${sel.address}`.toUpperCase();
            const depPrecedents = items.map((p, i) => {
                const nodeId = `${p.sheet}!${p.ref}`.toUpperCase();
                const ancestorIds = [originNodeId];
                const isCircular = ancestorIds.includes(nodeId);
                const formula = isCircular ? null : formulas[i];
                return {
                    ...p,
                    value: values[i],
                    depth: 1,
                    expanded: false,
                    childCount: 0,
                    nodeId,
                    treeKey: String(i),
                    ancestorIds,
                    isCircular,
                    hasFormula: !p.isRange && !isCircular && (hasDepsMap.get(nodeId) || false),
                    formula,
                };
            });
            traceNavRef.current = { index: 0, cancelled: false };
            openTraceWithData({
                formula: null,
                originCell: sel.address,
                originSheet: sel.sheet,
                originValue: sel.value,
                precedents: depPrecedents,
                mode: "dependents",
            });
        }
        catch (err) {
            console.error("Dependents trace error:", err);
        }
    }, [openTraceWithData]);
    const handleExpandDependentNode = useCallback(async (flatIndex) => {
        if (expandingRef.current)
            return;
        expandingRef.current = true;
        traceNavRef.current = { ...traceNavRef.current, cancelled: true };
        try {
            const data = traceDataRef.current;
            if (!data)
                return;
            const node = data.precedents[flatIndex];
            if (!node || node.isRange || node.expanded)
                return;
            const { status, items } = await readDirectDependents(node.ref, node.sheet);
            if (status !== "ok" || items.length === 0) {
                if (process.env.NODE_ENV !== "production" && status === "error") {
                    console.warn("Dependents expand failed for", node.ref, node.sheet);
                }
                return;
            }
            const { values, formulas } = await readPrecedentValuesAndFormulas(items);
            const singleCells = items.filter((c) => !c.isRange);
            const hasDeps = await checkCellsHaveDependents(singleCells);
            const hasDepsMap = new Map(singleCells.map((c, i) => [`${c.sheet}!${c.ref}`.toUpperCase(), hasDeps[i]]));
            const currentData = traceDataRef.current;
            if (!currentData)
                return;
            const currentNode = currentData.precedents[flatIndex];
            if (!currentNode || currentNode.nodeId !== node.nodeId || currentNode.expanded)
                return;
            const children = items.map((child, j) => {
                const nodeId = `${child.sheet}!${child.ref}`.toUpperCase();
                const ancestorIds = [...(node.ancestorIds || []), node.nodeId];
                const isCircular = ancestorIds.includes(nodeId);
                const formula = isCircular ? null : formulas[j];
                return {
                    ...child,
                    value: values[j],
                    depth: (node.depth || 0) + 1,
                    expanded: false,
                    childCount: 0,
                    nodeId,
                    treeKey: `${node.treeKey}/${j}`,
                    ancestorIds,
                    isCircular,
                    hasFormula: !child.isRange && !isCircular && (hasDepsMap.get(nodeId) || false),
                    formula,
                };
            });
            const precs = [...currentData.precedents];
            precs[flatIndex] = { ...precs[flatIndex], expanded: true, childCount: children.length };
            precs.splice(flatIndex + 1, 0, ...children);
            setTraceData({ ...currentData, precedents: precs });
            const currentSel = traceSelectedIndexRef.current;
            const adjustedSel = currentSel > flatIndex + 1 ? currentSel + children.length : currentSel;
            setTraceSelectedIndex(adjustedSel);
            traceNavRef.current = { index: adjustedSel, cancelled: false };
        }
        finally {
            expandingRef.current = false;
        }
    }, []);
    useEffect(() => {
        let disposed = false;
        function refreshTraceForSelection() {
            if (disposed || !traceOpenRef.current)
                return;
            if (liveRefreshTimerRef.current) {
                clearTimeout(liveRefreshTimerRef.current);
            }
            liveRefreshTimerRef.current = setTimeout(() => {
                liveRefreshTimerRef.current = null;
                if (disposed || !traceOpenRef.current)
                    return;
                void (async () => {
                    if (await shouldIgnoreTraceNavigationSelection())
                        return;
                    if (disposed || !traceOpenRef.current)
                        return;
                    void (traceModeRef.current === "dependents"
                        ? handleOpenDependents()
                        : handleOpenTrace());
                })();
            }, 180);
        }
        try {
            Office.context.document.addHandlerAsync(Office.EventType.DocumentSelectionChanged, refreshTraceForSelection);
        }
        catch {
            return undefined;
        }
        return () => {
            disposed = true;
            if (liveRefreshTimerRef.current) {
                clearTimeout(liveRefreshTimerRef.current);
                liveRefreshTimerRef.current = null;
            }
            try {
                Office.context.document.removeHandlerAsync(Office.EventType.DocumentSelectionChanged, { handler: refreshTraceForSelection });
            }
            catch {
                // Ignore cleanup failures from non-Office/test hosts.
            }
        };
    }, [handleOpenDependents, handleOpenTrace, shouldIgnoreTraceNavigationSelection]);
    const value = {
        traceOpen, traceData, traceSelectedIndex, traceMode,
        handleOpenTrace, handleOpenDependents, handleCloseTrace,
        handleGoHome, handleTraceSelect,
        handleExpandNode, handleExpandDependentNode, handleCollapseNode,
    };
    return _jsx(TraceContext.Provider, { value: value, children: children });
}
